import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { AlertTriangle, ChevronRight, Eye, CalendarClock, ClipboardCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusDot } from "@/components/ui/status-dot";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { isDateOverdue } from "@/lib/dateUtils";
import type { InspectionWithProfiles, Defect } from "@/hooks/useDashboardQuery";
import {
  type BentoView,
  type GetDisplayStatus,
  inspectionAssetName,
  makeFormatters,
  mppCardClasses,
  shortInspectionId,
  statusToDot,
} from "./dashboardBento.helpers";

/**
 * DashboardBentoDesktop — 12-col Bento (Stitch screen-04): Open-Defects col-4 +
 * Recent-Inspections-Table col-8 + MPP-Weekly col-12. Rein praesentational.
 */

interface DashboardBentoDesktopProps {
  recentInspections: InspectionWithProfiles[];
  defectsOpen: number;
  view: BentoView;
  getDisplayStatus: GetDisplayStatus;
}

/** Eine Open-Defects-Prioritaetszeile (overdue/due-today) mit border-l-4-Akzent. */
function PriorityRow({
  to,
  accent,
  label,
  title,
}: {
  to: string;
  accent: "overdue" | "dueToday";
  label: string;
  title: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "p-4 bg-surface rounded border-l-4 flex justify-between items-center group cursor-pointer hover:bg-surface-container-low transition-colors",
        accent === "overdue" ? "border-destructive" : "border-secondary",
      )}
    >
      <div className="min-w-0">
        <p
          className={cn(
            "font-bold text-label-caps",
            accent === "overdue" ? "text-destructive" : "text-secondary-foreground",
          )}
        >
          {label}
        </p>
        <p className="text-sm text-foreground mt-1 truncate">{title}</p>
      </div>
      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform shrink-0" />
    </Link>
  );
}

/** Urgency-Icon einer MPP-Karte (ausgelagert, haelt MppCard verzweigungsarm). */
function MppCardIcon({ urgent }: { urgent: boolean }) {
  return urgent ? <AlertTriangle className="h-5 w-5" /> : <CalendarClock className="h-5 w-5" />;
}

/** Eine MPP-Karte (urgent = ueberfaellig). */
function MppCard({
  inspection,
  onOpen,
  formatDueChip,
}: {
  inspection: InspectionWithProfiles;
  onOpen: () => void;
  formatDueChip: (iso: string | null) => string;
}) {
  const { t } = useTranslation();
  const urgent = isDateOverdue(inspection.due_date);
  const classes = mppCardClasses(urgent);
  const badgeLabel = urgent ? t("dashboard.bento.mppUrgent") : formatDueChip(inspection.due_date);
  const subtitle = inspection.checklists?.name ?? t("dashboard.bento.mppSubtitle");
  return (
    <div
      className={cn(
        "p-4 border border-outline-variant rounded relative group transition-colors",
        classes.container,
      )}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={cn("w-10 h-10 rounded flex items-center justify-center", classes.iconWrap)}>
          <MppCardIcon urgent={urgent} />
        </div>
        <span className={cn("px-2 py-1 text-label-caps rounded", classes.badge)}>{badgeLabel}</span>
      </div>
      <h4 className="font-bold text-foreground truncate">
        {inspectionAssetName(inspection, t("dashboard.unknownChecklist"))}
      </h4>
      <p className="text-data-tabular text-muted-foreground mt-1 truncate">{subtitle}</p>
      <div className={cn("mt-4 transition-opacity", classes.cta)}>
        <button
          type="button"
          onClick={onOpen}
          className="block w-full py-2 bg-primary text-primary-foreground text-label-caps text-center font-bold rounded"
        >
          {t("dashboard.bento.mppOpenInspection")}
        </button>
      </div>
    </div>
  );
}

export function DashboardBentoDesktop({
  recentInspections,
  defectsOpen,
  view,
  getDisplayStatus,
}: DashboardBentoDesktopProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatTimestamp, formatDueChip } = makeFormatters(i18n.language);
  const dueChip = (iso: string | null) => formatDueChip(iso, t("dashboard.bento.mppNoDueDate"));
  const noPriority = (d?: Defect) => d?.title ?? t("dashboard.noCriticalDefects");

  return (
    <div
      className="hidden md:grid grid-cols-12 gap-6 items-start"
      data-testid="dashboard-bento-desktop"
    >
      {/* Open Defects Summary — col-4 */}
      <div className="col-span-12 lg:col-span-4 bg-surface-container-lowest border border-outline-variant rounded p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {t("dashboard.bento.openDefects")}
          </h3>
          <span className="text-label-caps bg-destructive-container text-destructive-container-foreground px-2 py-1 rounded">
            {t("dashboard.bento.openDefectsTotal", { count: defectsOpen })}
          </span>
        </div>
        <div className="space-y-4">
          <PriorityRow
            to="/defects?status=overdue"
            accent="overdue"
            label={t("dashboard.bento.overdueGroup", { count: view.overdueDefects })}
            title={noPriority(view.overdueDefect)}
          />
          <PriorityRow
            to="/defects?status=openAll"
            accent="dueToday"
            label={t("dashboard.bento.dueTodayGroup", { count: view.dueTodayDefects })}
            title={noPriority(view.dueTodayDefect)}
          />
          <div className="mt-8">
            <div className="flex justify-between text-label-caps font-bold mb-2">
              <span>{t("dashboard.bento.resolutionRate")}</span>
              <span>{view.resolutionRate}%</span>
            </div>
            {/* TES-879: progressbar needs an accessible name (WCAG aria-progressbar-name) */}
            <Progress
              variant="meter"
              value={view.resolutionRate}
              aria-label={t("dashboard.bento.resolutionRate")}
            />
          </div>
        </div>
      </div>

      {/* Recent Inspections Table — col-8 */}
      <div className="col-span-12 lg:col-span-8 bg-surface-container-lowest border border-outline-variant rounded overflow-hidden">
        <div className="px-6 py-5 border-b border-outline-variant flex justify-between items-center">
          <h3 className="font-display text-lg font-semibold">
            {t("dashboard.bento.recentInspections")}
          </h3>
          <Link
            to="/history"
            className="inline-flex min-h-6 items-center text-primary font-bold text-label-caps hover:underline"
          >
            {t("dashboard.bento.viewAllHistory")}
          </Link>
        </div>
        {recentInspections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <ClipboardCheck className="h-10 w-10 mb-2 text-success" />
            <p className="text-sm">{t("dashboard.noPendingInspections")}</p>
          </div>
        ) : (
          <Table>
            <TableHeader zebra>
              <TableRow>
                <TableHead className="px-6 py-3 text-label-caps">
                  {t("dashboard.bento.colInspection")}
                </TableHead>
                <TableHead className="px-6 py-3 text-label-caps">
                  {t("dashboard.bento.colAsset")}
                </TableHead>
                <TableHead className="px-6 py-3 text-label-caps">
                  {t("dashboard.bento.colTimestamp")}
                </TableHead>
                <TableHead className="px-6 py-3 text-label-caps">
                  {t("dashboard.bento.colResult")}
                </TableHead>
                <TableHead className="px-6 py-3 text-label-caps text-right">
                  {t("dashboard.bento.colActions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody zebra>
              {recentInspections.slice(0, 6).map((inspection) => {
                const { state, resultKey } = statusToDot(getDisplayStatus(inspection));
                return (
                  <TableRow key={inspection.id}>
                    <TableCell className="px-6 py-4 font-bold text-primary">
                      {shortInspectionId(inspection.id)}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {inspectionAssetName(inspection, t("dashboard.unknownChecklist"))}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-muted-foreground">
                      {formatTimestamp(inspection.created_at)}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <StatusDot state={state} label={t(`dashboard.bento.${resultKey}`)} />
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => navigate(`/inspections/${inspection.id}`)}
                        aria-label={t("dashboard.bento.viewDetails")}
                        className="p-1 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* MPP Weekly Grid — col-12 */}
      <div className="col-span-12 bg-surface-container-lowest border border-outline-variant rounded p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-display text-lg font-semibold">{t("dashboard.bento.mppTitle")}</h3>
            <p className="text-sm text-muted-foreground">{t("dashboard.bento.mppSubtitle")}</p>
          </div>
        </div>
        {view.mppInspections.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("dashboard.bento.mppEmpty")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {view.mppInspections.map((inspection) => (
              <MppCard
                key={inspection.id}
                inspection={inspection}
                onOpen={() => navigate(`/inspections/${inspection.id}`)}
                formatDueChip={dueChip}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
